import { useState } from 'react';
import { usePublicPricing } from '../../hooks/usePublicPricing';
import {
  PRICING_CARD_COPY,
  SAVINGS_ASSUMPTION_FIELDS,
  SAVINGS_COPY,
  SAVINGS_DEFAULTS,
  SAVINGS_PRIMARY_FIELDS,
  savingsFieldId,
  type SavingsField,
  type SavingsFieldKey,
} from '../../data/pricing-content';
import {
  computeSavings,
  formatMoneyCents,
  parseSavingsInputs,
  type SavingsRawInputs,
} from '../../data/savings-calculator';

const INITIAL_INPUTS: SavingsRawInputs = {
  units: String(SAVINGS_DEFAULTS.units),
  rent: String(SAVINGS_DEFAULTS.rentCents / 100),
  zelleShare: String(SAVINGS_DEFAULTS.zelleSharePercent),
  managementFee: String(SAVINGS_DEFAULTS.managementFeePercent),
  hoursPerUnit: String(SAVINGS_DEFAULTS.hoursPerUnit),
  hourlyValue: String(SAVINGS_DEFAULTS.hourlyValueCents / 100),
  esignMonthly: String(SAVINGS_DEFAULTS.esignMonthlyCents / 100),
};

function Field({
  field,
  value,
  onChange,
}: {
  field: SavingsField;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = savingsFieldId(field.key);
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-[var(--landing-text-primary)] mb-1">
        {field.label}
        {field.hint && <span className="block font-normal text-[var(--landing-text-muted)]">{field.hint}</span>}
      </label>
      <input
        id={id}
        data-testid={`${id}-input`}
        type="number"
        inputMode="decimal"
        min={0}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full min-h-[44px] px-3 rounded-[var(--landing-radius)] border border-[var(--landing-border)] bg-[var(--landing-bg)] text-[var(--landing-text-primary)]"
      />
    </div>
  );
}

function Line({ label, cents, testId, strong = false }: { label: string; cents: number; testId: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-4 py-1 ${strong ? 'font-semibold text-[var(--landing-text-primary)]' : 'text-[var(--landing-text-muted)]'}`}>
      <dt>{label}</dt>
      <dd data-testid={testId}>{formatMoneyCents(cents)}</dd>
    </div>
  );
}

/**
 * Estimated monthly savings against the live price of the lowest plan ("starting at"; the pricing
 * endpoint serves plans in ascending unit price). Renders nothing until pricing has loaded with an
 * advertised plan, so the prerendered page never contains a figure (BR-107).
 */
export function SavingsCalculator(): JSX.Element | null {
  const state = usePublicPricing();
  const [raw, setRaw] = useState<SavingsRawInputs>(INITIAL_INPUTS);

  const lowestPlan = state.status === 'ready' ? state.pricing.plans[0] : undefined;
  if (!lowestPlan) return null;

  const inputs = parseSavingsInputs(raw);
  const result = inputs ? computeSavings(inputs, lowestPlan.unitPriceCents) : null;
  const setField = (key: SavingsFieldKey) => (value: string) => setRaw((prev) => ({ ...prev, [key]: value }));

  return (
    <section
      data-testid="savings-calculator"
      aria-labelledby="savings-heading"
      className="py-[var(--landing-section-py)] bg-[var(--landing-bg)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <h2 id="savings-heading" className="text-2xl md:text-3xl font-bold text-[var(--landing-text-primary)] mb-4">
            {SAVINGS_COPY.heading}
          </h2>
          <p className="text-lg text-[var(--landing-text-muted)]">{SAVINGS_COPY.intro}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-border)]">
            <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-4">{SAVINGS_COPY.inputsHeading}</h3>
            <div className="space-y-4">
              {SAVINGS_PRIMARY_FIELDS.map((field) => (
                <Field key={field.key} field={field} value={raw[field.key]} onChange={setField(field.key)} />
              ))}
            </div>
            <details className="mt-6" data-testid="savings-assumptions">
              <summary className="cursor-pointer min-h-[44px] flex items-center text-sm font-medium text-[var(--landing-accent)]">
                {SAVINGS_COPY.assumptionsSummary}
              </summary>
              <div className="space-y-4 mt-2">
                {SAVINGS_ASSUMPTION_FIELDS.map((field) => (
                  <Field key={field.key} field={field} value={raw[field.key]} onChange={setField(field.key)} />
                ))}
              </div>
            </details>
          </div>

          <div
            aria-live="polite"
            data-testid="savings-results"
            className="bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-accent)]"
          >
            {result ? (
              <>
                <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">{SAVINGS_COPY.savingsHeading}</h3>
                <dl className="mb-6">
                  <Line label={SAVINGS_COPY.managerLine} cents={result.managerCents} testId="savings-manager" />
                  <Line label={SAVINGS_COPY.timeLine} cents={result.timeCents} testId="savings-time" />
                  <Line label={SAVINGS_COPY.esignLine} cents={result.esignCents} testId="savings-esign" />
                  <Line label={SAVINGS_COPY.savingsTotal} cents={result.savingsCents} testId="savings-total" strong />
                </dl>
                <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">{SAVINGS_COPY.costHeading}</h3>
                <p className="text-sm text-[var(--landing-text-muted)] mb-2" data-testid="savings-plan-basis">
                  {SAVINGS_COPY.planBasis} {formatMoneyCents(lowestPlan.unitPriceCents)} {PRICING_CARD_COPY.perUnitMonth}
                </p>
                <dl className="mb-2">
                  <Line label={SAVINGS_COPY.platformLine} cents={result.platformCents} testId="savings-platform" />
                  <Line label={SAVINGS_COPY.achLine} cents={result.achCents} testId="savings-ach" />
                  <Line label={SAVINGS_COPY.costTotal} cents={result.costCents} testId="savings-cost" strong />
                </dl>
                <p className="text-sm text-[var(--landing-text-muted)] mb-6">{SAVINGS_COPY.zelleNote}</p>
                <dl className="border-t border-[var(--landing-border)] pt-4">
                  <Line label={SAVINGS_COPY.netMonthly} cents={result.netMonthlyCents} testId="savings-net-monthly" strong />
                  <Line label={SAVINGS_COPY.netAnnual} cents={result.netAnnualCents} testId="savings-net-annual" strong />
                </dl>
              </>
            ) : (
              <p data-testid="savings-invalid" className="text-[var(--landing-text-muted)]">
                {SAVINGS_COPY.invalidInputs}
              </p>
            )}
          </div>
        </div>

        <p className="max-w-3xl mx-auto mt-8 text-sm text-center text-[var(--landing-text-muted)]" data-testid="savings-footnote">
          {SAVINGS_COPY.footnote}
        </p>
      </div>
    </section>
  );
}
